import { managerialIsoDate } from '@/lib/managerial-calendar'
import type {
  OperatingCostCategory,
  OperatingCostCreditFeesPage,
  OperatingCostCreditInstallment,
  OperatingCostEntry,
  OperatingCostKind,
  OperatingCostMonthlyEntry,
  OperatingCostPaymentFeeOrigin,
  OperatingCostPaymentFeePosting,
  OperatingCostPaymentFeePostingsPage,
  OperatingCostPaymentFeeScope,
  OperatingCostPixFee,
  OperatingCostPixFeesPage,
  OperatingCostSalePosting,
  OperatingCostSalePostingCampaignOption,
  OperatingCostSalePostingsPage,
  OperatingCostSplitPosting,
  OperatingCostSplitPostingsPage,
  OperatingCostSplitScope,
  OperatingCostTotals,
  OperatingCostValueType,
  OperatingCostsSnapshot,
} from '@/types/api'

const KINDS: OperatingCostKind[] = ['fixed', 'variable']
const VALUE_TYPES: OperatingCostValueType[] = ['amount', 'percent']

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isNonNegativeFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isNullableNonNegativeNumber(value: unknown): value is number | null {
  return value === null || isNonNegativeFiniteNumber(value)
}

function isKind(value: unknown): value is OperatingCostKind {
  return typeof value === 'string' && KINDS.includes(value as OperatingCostKind)
}

function isValueType(value: unknown): value is OperatingCostValueType {
  return (
    typeof value === 'string' && VALUE_TYPES.includes(value as OperatingCostValueType)
  )
}

function isEntry(value: unknown): value is OperatingCostEntry {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (typeof value.subtitle !== 'string' || value.subtitle === '') return false
  if (
    value.competence_month !== undefined &&
    value.competence_month !== null &&
    (typeof value.competence_month !== 'string' ||
      !/^\d{4}-(0[1-9]|1[0-2])$/.test(value.competence_month))
  )
    return false
  return (
    isNullableNonNegativeNumber(value.amount) && isNullableNonNegativeNumber(value.rate)
  )
}

function isMonthlyEntry(value: unknown): value is OperatingCostMonthlyEntry {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (
    typeof value.year !== 'number' ||
    !Number.isInteger(value.year) ||
    value.year < 2000
  )
    return false
  if (
    typeof value.month !== 'number' ||
    !Number.isInteger(value.month) ||
    value.month < 1 ||
    value.month > 12
  ) {
    return false
  }
  return isNonNegativeFiniteNumber(value.amount)
}

function isCategory(value: unknown): value is OperatingCostCategory {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (typeof value.slug !== 'string' || value.slug === '') return false
  if (typeof value.name !== 'string' || value.name === '') return false
  if (!isKind(value.kind) || !isValueType(value.value_type)) return false
  if (typeof value.is_system !== 'boolean') return false
  if (typeof value.sort_order !== 'number' || !Number.isInteger(value.sort_order))
    return false
  if (!isNullableNonNegativeNumber(value.total_amount)) return false
  if (!isNullableNonNegativeNumber(value.total_rate)) return false
  if (!isNullableNonNegativeNumber(value.unit_amount)) return false
  if (
    value.fee_summary !== undefined &&
    value.fee_summary !== null &&
    typeof value.fee_summary !== 'string'
  ) {
    return false
  }
  if (!Array.isArray(value.entries) || !value.entries.every(isEntry)) return false
  return (
    Array.isArray(value.monthly_entries) && value.monthly_entries.every(isMonthlyEntry)
  )
}

function isTotals(value: unknown): value is OperatingCostTotals {
  if (!isRecord(value)) return false
  return (
    isNonNegativeFiniteNumber(value.fixed_amount) &&
    isNonNegativeFiniteNumber(value.variable_amount) &&
    isNonNegativeFiniteNumber(value.variable_percent) &&
    isNonNegativeFiniteNumber(value.amount)
  )
}

export function isOperatingCostsSnapshot(
  value: unknown
): value is OperatingCostsSnapshot {
  if (!isRecord(value)) return false
  if (!Array.isArray(value.categories) || !value.categories.every(isCategory))
    return false
  return isTotals(value.totals)
}

export function resolveOperatingCostsSnapshot(
  value: unknown
): OperatingCostsSnapshot | null {
  if (isOperatingCostsSnapshot(value)) return value
  if (!isRecord(value) || !('data' in value)) return null
  return isOperatingCostsSnapshot(value.data) ? value.data : null
}

export const OPERATING_COSTS_LOAD_ERROR =
  'Não foi possível carregar os custos operacionais.'
export const OPERATING_COSTS_NETWORK_ERROR =
  'Não foi possível concluir. Tente de novo em instantes.'
export const OPERATING_COSTS_CATEGORY_CREATED = 'Categoria criada.'
export const OPERATING_COSTS_CATEGORY_UPDATED = 'Categoria atualizada.'
export const OPERATING_COSTS_CATEGORY_DELETED = 'Categoria excluída.'
export const OPERATING_COSTS_ENTRY_CREATED = 'Lançamento criado.'
export const OPERATING_COSTS_ENTRY_UPDATED = 'Lançamento atualizado.'
export const OPERATING_COSTS_ENTRY_DELETED = 'Lançamento excluído.'
export const OPERATING_COSTS_UNIT_RATE_UPDATED = 'Tarifa da API WhatsApp atualizada.'
export const OPERATING_COSTS_MONTHLY_ENTRY_CREATED = 'Lançamento mensal criado.'
export const OPERATING_COSTS_MONTHLY_ENTRY_UPDATED = 'Lançamento mensal atualizado.'
export const OPERATING_COSTS_MONTHLY_ENTRY_DELETED = 'Lançamento mensal excluído.'
export const WHATSAPP_API_CATEGORY_SLUG = 'whatsapp_api'
export const AWS_SERVER_CATEGORY_SLUG = 'aws_server'
export const MARKETING_CATEGORY_SLUG = 'marketing'
export const THIRD_PARTY_SPLIT_COMMISSION_SLUG = 'third_party_split_commission'
export const DOEY_CATEGORY_SLUG = 'doey'
export const CREDIT_CARD_CATEGORY_SLUG = 'credit_card'
export const CREDIT_PERCENT_TAB_VALUE = 'credit_percent'
export const PIX_CATEGORY_DISPLAY_NAME = '% PIX'
export const CREDIT_PERCENT_TAB_LABEL = '% Crédito'
export const OPERATING_COSTS_PIX_DEFAULT_SAVED = 'Taxa padrão do PIX salva.'
export const OPERATING_COSTS_PIX_DEFAULT_DELETED = 'Taxa padrão do PIX excluída.'
export const OPERATING_COSTS_PIX_CAMPAIGN_SAVED = 'Taxa personalizada do PIX salva.'
export const OPERATING_COSTS_PIX_CAMPAIGN_DELETED = 'Taxa personalizada do PIX excluída.'
export const OPERATING_COSTS_CREDIT_DEFAULT_SAVED = 'Taxas padrão do cartão salvas.'
export const OPERATING_COSTS_CREDIT_DEFAULT_DELETED = 'Taxas padrão do cartão excluídas.'
export const OPERATING_COSTS_CREDIT_CAMPAIGN_SAVED =
  'Taxas personalizadas do cartão salvas.'
export const OPERATING_COSTS_CREDIT_CAMPAIGN_DELETED =
  'Taxas personalizadas do cartão excluídas.'
export const OPERATING_COST_YEAR_MIN = 2000
export const OPERATING_COST_YEAR_MAX_OFFSET = 10

export const OPERATING_COST_MONTH_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
]

function looksTechnical(message: string): boolean {
  return /unauthenticated|unauthorized|forbidden|validation failed|given data was invalid|server error|http \d|stack/i.test(
    message
  )
}

function firstValidationMessage(error: object): string {
  if (!('errors' in error) || error.errors === null || typeof error.errors !== 'object') {
    return ''
  }

  for (const value of Object.values(error.errors as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      const first = value.find((item) => typeof item === 'string' && item !== '')
      if (typeof first === 'string') {
        return first
      }
    }
    if (typeof value === 'string' && value !== '') {
      return value
    }
  }

  return ''
}

export function resolveOperatingCostsError(error: unknown): string {
  if (error instanceof Error && /abort/i.test(error.name)) {
    return OPERATING_COSTS_LOAD_ERROR
  }

  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = error.status
    const message =
      'message' in error && typeof error.message === 'string' ? error.message : ''

    if (typeof status === 'number' && status === 0) {
      return OPERATING_COSTS_NETWORK_ERROR
    }

    if (typeof status === 'number' && status === 422) {
      const fromErrors = firstValidationMessage(error)
      if (fromErrors !== '' && !looksTechnical(fromErrors)) {
        return fromErrors
      }
      if (message !== '' && !looksTechnical(message)) {
        return message
      }
      return 'Confira os dados informados e tente de novo.'
    }

    if (message !== '' && !looksTechnical(message)) {
      return message
    }
  }

  return OPERATING_COSTS_LOAD_ERROR
}

export function formatOperatingCostAmount(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })
    .format(value)
    .replace(/\u00a0|\u202f/g, ' ')
}

export function formatOperatingCostRate(value: number): string {
  const formatted = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value)
  return `${formatted}%`
}

export function formatCategoryTotal(category: OperatingCostCategory): string {
  if (
    category.slug === DOEY_CATEGORY_SLUG ||
    category.slug === CREDIT_CARD_CATEGORY_SLUG
  ) {
    return formatOperatingCostAmount(category.total_amount ?? 0)
  }
  if (typeof category.fee_summary === 'string' && category.fee_summary !== '') {
    return category.fee_summary
  }
  if (category.value_type === 'percent') {
    if (category.total_rate === null) {
      return '—'
    }
    return formatOperatingCostRate(category.total_rate)
  }
  return formatOperatingCostAmount(category.total_amount ?? 0)
}

export function parseOptionalAmountInput(value: string): number | null {
  if (value.trim() === '') return 0
  return parseAmountInput(value)
}

export function parseOptionalRateInput(value: string): number | null {
  if (value.trim() === '') return 0
  return parseRateInput(value)
}

export function formatRateInputValue(value: number): string {
  return String(value).replace('.', ',')
}

export function isCreditCardCategory(category: OperatingCostCategory): boolean {
  return category.slug === CREDIT_CARD_CATEGORY_SLUG
}

export function parseAmountInput(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed) || parsed < 0) return null
  return parsed
}

export function parseRateInput(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (normalized === '') return null
  const parsed = Number(normalized)
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return null
  return parsed
}

export function operatingCostYearMax(
  currentYear = Number(managerialIsoDate().slice(0, 4))
): number {
  return currentYear + OPERATING_COST_YEAR_MAX_OFFSET
}

export function parseYearInput(
  value: string,
  currentYear = Number(managerialIsoDate().slice(0, 4))
): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  if (
    !Number.isInteger(parsed) ||
    parsed < OPERATING_COST_YEAR_MIN ||
    parsed > operatingCostYearMax(currentYear)
  ) {
    return null
  }
  return parsed
}

export function parseMonthInput(value: string): number | null {
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 12) return null
  return parsed
}

export function formatOperatingCostMonth(month: number): string {
  return (
    OPERATING_COST_MONTH_OPTIONS.find((option) => option.value === month)?.label ??
    String(month)
  )
}

export function categoriesByKind(
  categories: OperatingCostCategory[],
  kind: OperatingCostKind
): OperatingCostCategory[] {
  return categories.filter((category) => category.kind === kind)
}

export function systemCategoriesByKind(
  categories: OperatingCostCategory[],
  kind: OperatingCostKind
): OperatingCostCategory[] {
  return categoriesByKind(categories, kind).filter((category) => category.is_system)
}

export function customCategoriesByKind(
  categories: OperatingCostCategory[],
  kind: OperatingCostKind
): OperatingCostCategory[] {
  return categoriesByKind(categories, kind).filter((category) => !category.is_system)
}

export const OTHERS_TAB_VALUE = 'outros'

export function kindMainTabLabel(kind: OperatingCostKind): string {
  return kind === 'fixed' ? 'Fixos' : 'Variáveis'
}

export function isDoeyCategory(category: OperatingCostCategory): boolean {
  return category.slug === DOEY_CATEGORY_SLUG
}

export function displayOperatingCostCategoryName(
  category: OperatingCostCategory
): string {
  if (isDoeyCategory(category)) {
    return PIX_CATEGORY_DISPLAY_NAME
  }

  return category.name
}

export function defaultInnerTab(systemCategories: OperatingCostCategory[]): string {
  return systemCategories[0]?.slug ?? OTHERS_TAB_VALUE
}

export function isWhatsAppApiCategory(category: OperatingCostCategory): boolean {
  return category.slug === WHATSAPP_API_CATEGORY_SLUG
}

export function isAwsServerCategory(category: OperatingCostCategory): boolean {
  return category.slug === AWS_SERVER_CATEGORY_SLUG
}

export function isThirdPartySplitCommissionCategory(
  category: OperatingCostCategory
): boolean {
  return category.slug === THIRD_PARTY_SPLIT_COMMISSION_SLUG
}

export function formatOperatingCostSplitOrigin(scope: OperatingCostSplitScope): string {
  return scope === 'campaign' ? 'Personalizado' : 'Padrão'
}

export function formatOperatingCostPaymentFeeOrigin(
  origin: OperatingCostPaymentFeeOrigin
): string {
  return origin === 'credit' ? 'Crédito' : 'PIX'
}

export function formatOperatingCostPaymentFeeScope(
  scope: OperatingCostPaymentFeeScope
): string {
  return scope === 'campaign' ? 'Campanha' : 'Padrão'
}

export function formatOperatingCostPaymentFeeApplied(
  posting: OperatingCostPaymentFeePosting
): string {
  const parts = [formatOperatingCostRate(posting.rate)]
  if (posting.fixed_amount > 0) {
    parts.push(formatOperatingCostAmount(posting.fixed_amount))
  }
  if (posting.surcharge_rate > 0) {
    parts.push(formatOperatingCostRate(posting.surcharge_rate))
  }
  if (posting.surcharge_amount > 0) {
    parts.push(formatOperatingCostAmount(posting.surcharge_amount))
  }
  const applied = parts.join(' + ')
  return `${formatOperatingCostPaymentFeeScope(posting.fee_scope)} · ${applied}`
}

export function formatOperatingCostDateTime(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'America/Sao_Paulo',
  }).format(parsed)
}

function isSalePosting(value: unknown): value is OperatingCostSalePosting {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (
    typeof value.payment_id !== 'number' ||
    !Number.isInteger(value.payment_id) ||
    value.payment_id <= 0
  ) {
    return false
  }
  if (
    typeof value.campaign_id !== 'number' ||
    !Number.isInteger(value.campaign_id) ||
    value.campaign_id <= 0
  ) {
    return false
  }
  if (
    typeof value.donor_id !== 'number' ||
    !Number.isInteger(value.donor_id) ||
    value.donor_id <= 0
  ) {
    return false
  }
  if (typeof value.campaign_name !== 'string' || value.campaign_name === '') return false
  if (typeof value.donor_name !== 'string' || value.donor_name === '') return false
  if (!isNonNegativeFiniteNumber(value.amount)) return false
  if (!isNonNegativeFiniteNumber(value.sale_amount)) return false
  return typeof value.posted_at === 'string' && value.posted_at !== ''
}

function isCampaignOption(
  value: unknown
): value is OperatingCostSalePostingCampaignOption {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  return typeof value.name === 'string' && value.name !== ''
}

export function isOperatingCostSalePostingsPage(
  value: unknown
): value is OperatingCostSalePostingsPage {
  if (!isRecord(value)) return false
  if (!isNullableNonNegativeNumber(value.unit_amount)) return false
  if (!isNonNegativeFiniteNumber(value.filtered_total)) return false
  if (!Array.isArray(value.postings) || !value.postings.every(isSalePosting)) return false
  if (!isRecord(value.meta)) return false
  if (
    typeof value.meta.current_page !== 'number' ||
    !Number.isInteger(value.meta.current_page)
  )
    return false
  if (typeof value.meta.last_page !== 'number' || !Number.isInteger(value.meta.last_page))
    return false
  if (typeof value.meta.per_page !== 'number' || !Number.isInteger(value.meta.per_page))
    return false
  if (typeof value.meta.total !== 'number' || !Number.isInteger(value.meta.total))
    return false
  if (!isRecord(value.filter_options) || !Array.isArray(value.filter_options.campaigns))
    return false
  return value.filter_options.campaigns.every(isCampaignOption)
}

export function resolveOperatingCostSalePostingsPage(
  value: unknown
): OperatingCostSalePostingsPage | null {
  if (isOperatingCostSalePostingsPage(value)) return value
  if (!isRecord(value) || !('data' in value)) return null
  return isOperatingCostSalePostingsPage(value.data) ? value.data : null
}

function isSplitScope(value: unknown): value is OperatingCostSplitScope {
  return value === 'global' || value === 'campaign'
}

function isSplitPosting(value: unknown): value is OperatingCostSplitPosting {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (
    typeof value.payment_id !== 'number' ||
    !Number.isInteger(value.payment_id) ||
    value.payment_id <= 0
  ) {
    return false
  }
  if (
    typeof value.campaign_id !== 'number' ||
    !Number.isInteger(value.campaign_id) ||
    value.campaign_id <= 0
  ) {
    return false
  }
  if (
    typeof value.donor_id !== 'number' ||
    !Number.isInteger(value.donor_id) ||
    value.donor_id <= 0
  ) {
    return false
  }
  if (value.institution_id !== null) {
    if (
      typeof value.institution_id !== 'number' ||
      !Number.isInteger(value.institution_id) ||
      value.institution_id <= 0
    ) {
      return false
    }
  }
  if (
    typeof value.split_id !== 'number' ||
    !Number.isInteger(value.split_id) ||
    value.split_id <= 0
  ) {
    return false
  }
  if (!isSplitScope(value.split_scope)) return false
  if (typeof value.split_name !== 'string' || value.split_name === '') return false
  if (!isNonNegativeFiniteNumber(value.split_percentage)) return false
  if (typeof value.institution_name !== 'string' || value.institution_name === '')
    return false
  if (typeof value.campaign_name !== 'string' || value.campaign_name === '') return false
  if (typeof value.donor_name !== 'string' || value.donor_name === '') return false
  if (!isNonNegativeFiniteNumber(value.amount)) return false
  if (!isNonNegativeFiniteNumber(value.sale_amount)) return false
  return typeof value.posted_at === 'string' && value.posted_at !== ''
}

export function isOperatingCostSplitPostingsPage(
  value: unknown
): value is OperatingCostSplitPostingsPage {
  if (!isRecord(value)) return false
  if (!isNonNegativeFiniteNumber(value.filtered_total)) return false
  if (!Array.isArray(value.postings) || !value.postings.every(isSplitPosting))
    return false
  if (!isRecord(value.meta)) return false
  if (
    typeof value.meta.current_page !== 'number' ||
    !Number.isInteger(value.meta.current_page)
  )
    return false
  if (typeof value.meta.last_page !== 'number' || !Number.isInteger(value.meta.last_page))
    return false
  if (typeof value.meta.per_page !== 'number' || !Number.isInteger(value.meta.per_page))
    return false
  if (typeof value.meta.total !== 'number' || !Number.isInteger(value.meta.total))
    return false
  if (!isRecord(value.filter_options) || !Array.isArray(value.filter_options.campaigns))
    return false
  if (!Array.isArray(value.filter_options.institutions)) return false
  return (
    value.filter_options.campaigns.every(isCampaignOption) &&
    value.filter_options.institutions.every(isCampaignOption)
  )
}

export function resolveOperatingCostSplitPostingsPage(
  value: unknown
): OperatingCostSplitPostingsPage | null {
  if (isOperatingCostSplitPostingsPage(value)) return value
  if (!isRecord(value) || !('data' in value)) return null
  return isOperatingCostSplitPostingsPage(value.data) ? value.data : null
}

export function resolveOperatingProfitSplitPostingsPage(
  value: unknown
): OperatingCostSplitPostingsPage | null {
  return resolveOperatingCostSplitPostingsPage(value)
}

function isPaymentFeeOrigin(value: unknown): value is OperatingCostPaymentFeeOrigin {
  return value === 'pix' || value === 'credit'
}

function isPaymentFeeScope(value: unknown): value is OperatingCostPaymentFeeScope {
  return value === 'default' || value === 'campaign'
}

function isPaymentFeePosting(value: unknown): value is OperatingCostPaymentFeePosting {
  if (!isRecord(value)) return false
  if (typeof value.id !== 'number' || !Number.isInteger(value.id) || value.id <= 0)
    return false
  if (
    typeof value.payment_id !== 'number' ||
    !Number.isInteger(value.payment_id) ||
    value.payment_id <= 0
  ) {
    return false
  }
  if (
    typeof value.campaign_id !== 'number' ||
    !Number.isInteger(value.campaign_id) ||
    value.campaign_id <= 0
  ) {
    return false
  }
  if (
    typeof value.donor_id !== 'number' ||
    !Number.isInteger(value.donor_id) ||
    value.donor_id <= 0
  ) {
    return false
  }
  if (value.institution_id !== null) {
    if (
      typeof value.institution_id !== 'number' ||
      !Number.isInteger(value.institution_id) ||
      value.institution_id <= 0
    ) {
      return false
    }
  }
  if (!isPaymentFeeOrigin(value.origin) || !isPaymentFeeScope(value.fee_scope))
    return false
  if (value.installment !== null) {
    if (
      typeof value.installment !== 'number' ||
      !Number.isInteger(value.installment) ||
      value.installment < 1
    ) {
      return false
    }
  }
  if (!isNonNegativeFiniteNumber(value.rate)) return false
  if (!isNonNegativeFiniteNumber(value.fixed_amount)) return false
  if (!isNonNegativeFiniteNumber(value.surcharge_rate)) return false
  if (!isNonNegativeFiniteNumber(value.surcharge_amount)) return false
  if (typeof value.institution_name !== 'string' || value.institution_name === '')
    return false
  if (typeof value.campaign_name !== 'string' || value.campaign_name === '') return false
  if (typeof value.donor_name !== 'string' || value.donor_name === '') return false
  if (!isNonNegativeFiniteNumber(value.amount)) return false
  if (!isNonNegativeFiniteNumber(value.sale_amount)) return false
  return typeof value.posted_at === 'string' && value.posted_at !== ''
}

export function isOperatingCostPaymentFeePostingsPage(
  value: unknown
): value is OperatingCostPaymentFeePostingsPage {
  if (!isRecord(value)) return false
  if (!isNonNegativeFiniteNumber(value.filtered_total)) return false
  if (!Array.isArray(value.postings) || !value.postings.every(isPaymentFeePosting))
    return false
  if (!isRecord(value.meta)) return false
  if (
    typeof value.meta.current_page !== 'number' ||
    !Number.isInteger(value.meta.current_page)
  )
    return false
  if (typeof value.meta.last_page !== 'number' || !Number.isInteger(value.meta.last_page))
    return false
  if (typeof value.meta.per_page !== 'number' || !Number.isInteger(value.meta.per_page))
    return false
  if (typeof value.meta.total !== 'number' || !Number.isInteger(value.meta.total))
    return false
  if (!isRecord(value.filter_options) || !Array.isArray(value.filter_options.campaigns))
    return false
  if (!Array.isArray(value.filter_options.institutions)) return false
  return (
    value.filter_options.campaigns.every(isCampaignOption) &&
    value.filter_options.institutions.every(isCampaignOption)
  )
}

export function resolveOperatingCostPaymentFeePostingsPage(
  value: unknown
): OperatingCostPaymentFeePostingsPage | null {
  if (isOperatingCostPaymentFeePostingsPage(value)) return value
  if (!isRecord(value) || !('data' in value)) return null
  return isOperatingCostPaymentFeePostingsPage(value.data) ? value.data : null
}

function isPixFee(value: unknown): value is OperatingCostPixFee {
  if (!isRecord(value)) return false
  if (value.campaign_id !== null) {
    if (
      typeof value.campaign_id !== 'number' ||
      !Number.isInteger(value.campaign_id) ||
      value.campaign_id <= 0
    ) {
      return false
    }
  }
  if (
    value.campaign_name !== null &&
    (typeof value.campaign_name !== 'string' || value.campaign_name === '')
  ) {
    return false
  }
  if (
    value.institution_name !== null &&
    (typeof value.institution_name !== 'string' || value.institution_name === '')
  ) {
    return false
  }
  return (
    isNonNegativeFiniteNumber(value.rate) && isNonNegativeFiniteNumber(value.fixed_amount)
  )
}

export function isOperatingCostPixFeesPage(
  value: unknown
): value is OperatingCostPixFeesPage {
  if (!isRecord(value)) return false
  if (value.default !== null && !isPixFee(value.default)) return false
  return Array.isArray(value.campaigns) && value.campaigns.every(isPixFee)
}

export function resolveOperatingCostPixFeesPage(
  value: unknown
): OperatingCostPixFeesPage | null {
  if (isOperatingCostPixFeesPage(value)) return value
  if (!isRecord(value) || !('data' in value)) return null
  return isOperatingCostPixFeesPage(value.data) ? value.data : null
}

function isCreditInstallment(value: unknown): value is OperatingCostCreditInstallment {
  if (!isRecord(value)) return false
  if (typeof value.installment !== 'number' || !Number.isInteger(value.installment))
    return false
  if (value.installment < 1 || value.installment > 12) return false
  return (
    isNonNegativeFiniteNumber(value.rate) &&
    isNonNegativeFiniteNumber(value.surcharge_rate) &&
    isNonNegativeFiniteNumber(value.surcharge_amount) &&
    isNonNegativeFiniteNumber(value.total_rate)
  )
}

export function isOperatingCostCreditFeesPage(
  value: unknown
): value is OperatingCostCreditFeesPage {
  if (!isRecord(value)) return false
  if (value.default !== null) {
    if (!isRecord(value.default) || !Array.isArray(value.default.installments))
      return false
    if (!value.default.installments.every(isCreditInstallment)) return false
  }
  if (!Array.isArray(value.campaigns)) return false
  return value.campaigns.every((item) => {
    if (!isRecord(item)) return false
    if (
      typeof item.campaign_id !== 'number' ||
      !Number.isInteger(item.campaign_id) ||
      item.campaign_id <= 0
    ) {
      return false
    }
    if (typeof item.campaign_name !== 'string' || item.campaign_name === '') return false
    if (typeof item.institution_name !== 'string' || item.institution_name === '')
      return false
    return (
      Array.isArray(item.installments) && item.installments.every(isCreditInstallment)
    )
  })
}

export function resolveOperatingCostCreditFeesPage(
  value: unknown
): OperatingCostCreditFeesPage | null {
  if (isOperatingCostCreditFeesPage(value)) return value
  if (!isRecord(value) || !('data' in value)) return null
  return isOperatingCostCreditFeesPage(value.data) ? value.data : null
}
